import { JsonValue, MessageType } from "@protobuf-ts/runtime";
import { QueryKey } from "@tanstack/react-query";
import { z } from "zod";

import { MutationDefinitionOptions, QueryDefinitionOptions } from "../types";

import { createMutationBaseMixin } from "./base-mutation";
import { createQueryBaseMixin } from "./base-query";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const $UNSAFE_IgnoreProto = Symbol("ignoreProto") as unknown as MessageType<any>;

interface ResponseProtoOptions<
  TransformedResult extends object,
  ResponseMessageType extends MessageType<TransformedResult>,
> {
  responseMessageType: ResponseMessageType;
}

interface ParamsProtoOptions<
  ParamsSchema extends z.ZodTypeAny,
  ParamsMessageType extends MessageType<z.infer<ParamsSchema>>,
> {
  paramsMessageType: ParamsMessageType;
}

type ProtoOptions<
  ParamsSchema extends z.ZodTypeAny,
  TransformedResult extends object,
  ParamsMessageType extends MessageType<z.infer<ParamsSchema>>,
  ResponseMessageType extends MessageType<TransformedResult>,
> = ParamsSchema extends z.ZodVoid
  ? ResponseProtoOptions<TransformedResult, ResponseMessageType>
  : ParamsProtoOptions<ParamsSchema, ParamsMessageType> &
      ResponseProtoOptions<TransformedResult, ResponseMessageType>;

export function createQueryProtoMixin<
  TQueryKey extends QueryKey,
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  ResponseSchema extends z.ZodTypeAny,
  TransformedResult extends object = z.infer<ResponseSchema>,
>(
  originalDefinitionOptions: QueryDefinitionOptions<
    TQueryKey,
    Path,
    ParamsSchema,
    ResponseSchema,
    TransformedResult
  >
) {
  return {
    withProtos<
      ParamsMessageType extends MessageType<z.infer<ParamsSchema>>,
      ResponseMessageType extends MessageType<TransformedResult>,
    >(
      protoOptions: ProtoOptions<
        ParamsSchema,
        TransformedResult,
        ParamsMessageType,
        ResponseMessageType
      >
    ) {
      return createQueryBaseMixin({
        ...originalDefinitionOptions,
        responseSchema: wrapZodWithProto(
          originalDefinitionOptions.responseSchema,
          protoOptions.responseMessageType
        ),
        paramsSchema: originalDefinitionOptions.paramsSchema
          ? wrapZodWithProto(
              originalDefinitionOptions.paramsSchema,
              (protoOptions as ParamsProtoOptions<ParamsSchema, ParamsMessageType>)
                .paramsMessageType
            )
          : undefined,
      });
    },
  };
}

export function createMutationProtoMixin<
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  ResponseSchema extends z.ZodTypeAny,
  TransformedResult extends object = z.infer<ResponseSchema>,
>(
  originalDefinitionOptions: MutationDefinitionOptions<
    Path,
    ParamsSchema,
    ResponseSchema,
    TransformedResult
  >
) {
  return {
    withProtos<
      ParamsMessageType extends MessageType<z.infer<ParamsSchema>>,
      ResponseMessageType extends MessageType<TransformedResult>,
    >(
      protoOptions: ProtoOptions<
        ParamsSchema,
        TransformedResult,
        ParamsMessageType,
        ResponseMessageType
      >
    ) {
      return createMutationBaseMixin({
        ...originalDefinitionOptions,
        responseSchema: wrapZodWithProto(
          originalDefinitionOptions.responseSchema,
          protoOptions.responseMessageType
        ),
        paramsSchema: wrapZodWithProto(
          originalDefinitionOptions.paramsSchema,
          (protoOptions as ParamsProtoOptions<ParamsSchema, ParamsMessageType>).paramsMessageType
        ),
      });
    },
  };
}

function wrapZodWithProto<T extends z.ZodTypeAny>(schema: T, proto: MessageType<z.infer<T>>) {
  if (proto === $UNSAFE_IgnoreProto) {
    return schema;
  }

  return z.preprocess((value) => {
    return proto.fromJson(value as JsonValue, { ignoreUnknownFields: true });
  }, schema) as unknown as T;
}
